import type { Attendee, Flight, Invite } from "./types";

/**
 * API 回傳的資料形狀——前後端共用。
 * 公開端點只給「看板 / 選位圖」需要的最小欄位：
 * 備註（忌口等隱私）與登機證代碼只出現在本人 pass 端點和 admin 端點。
 */

export interface FlightCounts {
  confirmed: number;
  standby: number;
  boarded: number;
}

/** 航班列表（/ 與後台首頁） */
export interface BoardFlight {
  flight: Flight;
  counts: FlightCounts;
}

/** 公開的乘客欄位（座位圖點座位看是誰） */
export interface PublicAttendee {
  id: string;
  name: string;
  industry?: string;
  seat?: string;
  status: "confirmed" | "standby";
  createdAt: string;
}

export interface InviteView extends Invite {
  /** 這筆接龍名額已用掉幾位 */
  used: number;
}

/** 活動頁 / 報名頁 / 選位頁需要的一切 */
export interface FlightView {
  flight: Flight;
  attendees: PublicAttendee[];
  invites: InviteView[];
}

/** 登機證頁：本人完整資料 + 同行者 */
export interface PassView {
  attendee: Attendee;
  flight: Flight;
  companions: { id: string; name: string; seat?: string; status: string; passToken: string }[];
  /** 同一桌的其他人（姓名 + 產業），登機證上顯示「你這桌坐了誰」 */
  tableMates: PublicAttendee[];
}

/** 後台乘客名單：完整欄位（含備註、票號） */
export interface ManifestView {
  flight: Flight;
  attendees: Attendee[];
  invites: InviteView[];
}

export type SignupReason =
  | "flight-missing"
  | "closed"
  | "invite-required"
  | "quota-exceeded"
  | "seat-taken"
  | "seat-mismatch"
  | "not-enough-seats"
  | "industry-required";

export type SignupResult =
  | { ok: true; attendees: Attendee[] }
  | { ok: false; reason: SignupReason };

export type ScanResult =
  | { kind: "ok"; attendee: PublicAttendee }
  | { kind: "already"; attendee: PublicAttendee }
  | { kind: "standby"; attendee: PublicAttendee }
  | { kind: "wrong-flight"; attendee: PublicAttendee }
  | { kind: "invalid" };

// ── view 上的純函式 ────────────────────────────────────────

export function takenSeatsOf(view: FlightView): Map<string, PublicAttendee> {
  const map = new Map<string, PublicAttendee>();
  for (const a of view.attendees) {
    if (a.status === "confirmed" && a.seat) map.set(a.seat, a);
  }
  return map;
}

export function confirmedCountOf(view: FlightView): number {
  return view.attendees.filter((a) => a.status === "confirmed").length;
}

export function standbyCountOf(view: FlightView): number {
  return view.attendees.filter((a) => a.status === "standby").length;
}

export function inviteByName(view: FlightView, name: string): InviteView | undefined {
  const q = name.trim().toLowerCase();
  if (!q) return undefined;
  return view.invites.find((i) => i.name.toLowerCase() === q);
}

// ── 桌邊聊天 ────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  name: string;
  seat?: string;
  /** 從座位代號拆出的桌號，用來顯示「3 桌的林小美說」 */
  table?: string;
  body: string;
  createdAt: string;
  /** 是不是讀取者自己發的（由 server 依 token 判斷） */
  mine?: boolean;
  /** 這則 @ 到的人的姓名，用來把那幾段文字標出來 */
  mentions?: string[];
  /** 這則有沒有 @ 到讀取者本人（由 server 依 token 判斷） */
  mentionsMe?: boolean;
}

/** 可以被 @ 的人：姓名 + 桌號（同名時靠桌號分辨） */
export interface ChatPerson {
  name: string;
  table?: string;
}

export interface ChatView {
  messages: ChatMessage[];
  myTable?: string;
  myName: string;
  canPost: boolean;
  /** 打 @ 時可以挑的人 —— 只有名單上的名字 tag 得到 */
  people: ChatPerson[];
}
